import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useWorkspaceStore, useUIStore } from '@/store';
import { workspaceApi } from '@/api';
import {
  WorkspaceCreateData,
  WorkspaceUpdateData,
  WorkspaceMemberAddData,
  WorkspaceMemberUpdateData,
  WorkspaceInviteData,
} from '@/types';

/**
 * Workspaces hook
 * Provides workspace management functionality
 */
export const useWorkspacesHook = () => {
  const setWorkspaces = useWorkspaceStore((state) => state.setWorkspaces);
  const addWorkspace = useWorkspaceStore((state) => state.addWorkspace);
  const updateWorkspaceState = useWorkspaceStore((state) => state.updateWorkspace);
  const removeWorkspace = useWorkspaceStore((state) => state.removeWorkspace);
  const setSelectedWorkspace = useWorkspaceStore((state) => state.setSelectedWorkspace);
  const setWorkspaceMembers = useWorkspaceStore((state) => state.setWorkspaceMembers);
  const removeWorkspaceMemberState = useWorkspaceStore((state) => state.removeWorkspaceMember);
  const updateMemberRoleState = useWorkspaceStore((state) => state.updateMemberRole);
  const setWorkspaceProjects = useWorkspaceStore((state) => state.setWorkspaceProjects);
  const setWorkspaceDashboard = useWorkspaceStore((state) => state.setWorkspaceDashboard);
  const setReceivedInvitations = useWorkspaceStore((state) => state.setReceivedInvitations);
  const setWorkspaceInvitations = useWorkspaceStore((state) => state.setWorkspaceInvitations);
  const removeReceivedInvitation = useWorkspaceStore((state) => state.removeReceivedInvitation);
  const removeWorkspaceInvitation = useWorkspaceStore((state) => state.removeWorkspaceInvitation);
  const setLoading = useWorkspaceStore((state) => state.setLoading);
  const setLoadingMembers = useWorkspaceStore((state) => state.setLoadingMembers);
  const setLoadingProjects = useWorkspaceStore((state) => state.setLoadingProjects);
  const setLoadingDashboard = useWorkspaceStore((state) => state.setLoadingDashboard);
  const setLoadingInvitations = useWorkspaceStore((state) => state.setLoadingInvitations);
  const showLoader = useUIStore((state) => state.showLoader);
  const hideLoader = useUIStore((state) => state.hideLoader);

  // ========== Workspace CRUD ==========

  /**
   * Fetch all workspaces
   * Note: Does not show error toast for initial fetch failures to avoid
   * spamming errors for new users who haven't created workspaces yet
   */
  const fetchWorkspaces = useCallback(async (showError = false) => {
    setLoading(true);
    try {
      const workspaces = await workspaceApi.getWorkspaces();
      setWorkspaces(workspaces);
      return workspaces;
    } catch (error: any) {
      // Only show error if explicitly requested (manual refresh, etc.)
      if (showError) {
        toast.error(error.message || 'Failed to fetch workspaces');
      }
      console.error('Failed to fetch workspaces:', error);
      setWorkspaces([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [setWorkspaces, setLoading]);

  /**
   * Fetch workspace by ID
   */
  const fetchWorkspaceById = useCallback(
    async (workspaceId: string) => {
      setLoading(true);
      try {
        const workspace = await workspaceApi.getWorkspaceById(workspaceId);
        setSelectedWorkspace(workspace);
        return workspace;
      } catch (error: any) {
        toast.error(error.message || 'Failed to fetch workspace');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [setSelectedWorkspace, setLoading]
  );

  /**
   * Fetch workspace by slug
   */
  const fetchWorkspaceBySlug = useCallback(
    async (slug: string) => {
      setLoading(true);
      try {
        const workspace = await workspaceApi.getWorkspaceBySlug(slug);
        setSelectedWorkspace(workspace);
        return workspace;
      } catch (error: any) {
        toast.error(error.message || 'Failed to fetch workspace');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [setSelectedWorkspace, setLoading]
  );

  /**
   * Create a new workspace
   */
  const createWorkspace = useCallback(
    async (data: WorkspaceCreateData) => {
      showLoader('Creating workspace...');
      try {
        const workspace = await workspaceApi.createWorkspace(data);
        addWorkspace(workspace);
        toast.success('Workspace created successfully');
        return workspace;
      } catch (error: any) {
        toast.error(error.message || 'Failed to create workspace');
        return null;
      } finally {
        hideLoader();
      }
    },
    [addWorkspace, showLoader, hideLoader]
  );

  /**
   * Update a workspace
   */
  const updateWorkspace = useCallback(
    async (workspaceId: string, data: WorkspaceUpdateData) => {
      showLoader('Updating workspace...');
      try {
        await workspaceApi.updateWorkspace(workspaceId, data);
        updateWorkspaceState(workspaceId, data);
        toast.success('Workspace updated successfully');
        return true;
      } catch (error: any) {
        toast.error(error.message || 'Failed to update workspace');
        return false;
      } finally {
        hideLoader();
      }
    },
    [updateWorkspaceState, showLoader, hideLoader]
  );

  /**
   * Delete a workspace
   */
  const deleteWorkspace = useCallback(
    async (workspaceId: string) => {
      showLoader('Deleting workspace...');
      try {
        await workspaceApi.deleteWorkspace(workspaceId);
        removeWorkspace(workspaceId);
        toast.success('Workspace deleted successfully');
        return true;
      } catch (error: any) {
        toast.error(error.message || 'Failed to delete workspace');
        return false;
      } finally {
        hideLoader();
      }
    },
    [removeWorkspace, showLoader, hideLoader]
  );

  // ========== Workspace Members ==========

  /**
   * Fetch workspace members
   */
  const fetchWorkspaceMembers = useCallback(
    async (workspaceId: string) => {
      setLoadingMembers(true);
      try {
        const members = await workspaceApi.getWorkspaceMembers(workspaceId);
        setWorkspaceMembers(members);
        return members;
      } catch (error: any) {
        toast.error(error.message || 'Failed to fetch workspace members');
        return [];
      } finally {
        setLoadingMembers(false);
      }
    },
    [setWorkspaceMembers, setLoadingMembers]
  );

  /**
   * Add a member to workspace
   */
  const addWorkspaceMember = useCallback(
    async (workspaceId: string, data: WorkspaceMemberAddData) => {
      showLoader('Adding member...');
      try {
        await workspaceApi.addWorkspaceMember(workspaceId, data);
        // Refresh members list
        await fetchWorkspaceMembers(workspaceId);
        toast.success('Member added successfully');
        return true;
      } catch (error: any) {
        toast.error(error.message || 'Failed to add member');
        return false;
      } finally {
        hideLoader();
      }
    },
    [showLoader, hideLoader, fetchWorkspaceMembers]
  );

  /**
   * Update member role
   */
  const updateMemberRole = useCallback(
    async (workspaceId: string, userId: string, data: WorkspaceMemberUpdateData) => {
      showLoader('Updating member role...');
      try {
        await workspaceApi.updateWorkspaceMember(workspaceId, userId, data);
        updateMemberRoleState(userId, data.role);
        toast.success('Member role updated successfully');
        return true;
      } catch (error: any) {
        toast.error(error.message || 'Failed to update member role');
        return false;
      } finally {
        hideLoader();
      }
    },
    [updateMemberRoleState, showLoader, hideLoader]
  );

  /**
   * Remove a member from workspace
   */
  const removeWorkspaceMember = useCallback(
    async (workspaceId: string, userId: string) => {
      showLoader('Removing member...');
      try {
        await workspaceApi.removeWorkspaceMember(workspaceId, userId);
        removeWorkspaceMemberState(userId);
        toast.success('Member removed successfully');
        return true;
      } catch (error: any) {
        toast.error(error.message || 'Failed to remove member');
        return false;
      } finally {
        hideLoader();
      }
    },
    [removeWorkspaceMemberState, showLoader, hideLoader]
  );

  /**
   * Leave a workspace
   */
  const leaveWorkspace = useCallback(
    async (workspaceId: string) => {
      showLoader('Leaving workspace...');
      try {
        await workspaceApi.leaveWorkspace(workspaceId);
        removeWorkspace(workspaceId);
        toast.success('Left workspace successfully');
        return true;
      } catch (error: any) {
        toast.error(error.message || 'Failed to leave workspace');
        return false;
      } finally {
        hideLoader();
      }
    },
    [removeWorkspace, showLoader, hideLoader]
  );

  // ========== Workspace Invitations ==========

  /**
   * Invite user to workspace
   */
  const inviteUserToWorkspace = useCallback(
    async (workspaceId: string, data: WorkspaceInviteData) => {
      showLoader('Sending invitation...');
      try {
        const invitation = await workspaceApi.inviteUserToWorkspace(workspaceId, data);
        toast.success('Invitation sent successfully');
        return invitation;
      } catch (error: any) {
        toast.error(error.message || 'Failed to send invitation');
        return null;
      } finally {
        hideLoader();
      }
    },
    [showLoader, hideLoader]
  );

  /**
   * Fetch workspace invitations (admin view)
   */
  const fetchWorkspaceInvitations = useCallback(
    async (workspaceId: string) => {
      setLoadingInvitations(true);
      try {
        const invitations = await workspaceApi.getWorkspaceInvitations(workspaceId);
        setWorkspaceInvitations(invitations);
        return invitations;
      } catch (error: any) {
        toast.error(error.message || 'Failed to fetch invitations');
        return [];
      } finally {
        setLoadingInvitations(false);
      }
    },
    [setWorkspaceInvitations, setLoadingInvitations]
  );

  /**
   * Fetch received workspace invitations (user view)
   */
  const fetchReceivedInvitations = useCallback(async () => {
    setLoadingInvitations(true);
    try {
      const invitations = await workspaceApi.getReceivedWorkspaceInvitations();
      setReceivedInvitations(invitations);
      return invitations;
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch invitations');
      return [];
    } finally {
      setLoadingInvitations(false);
    }
  }, [setReceivedInvitations, setLoadingInvitations]);

  /**
   * Accept workspace invitation
   */
  const acceptInvitation = useCallback(
    async (invitationId: string) => {
      showLoader('Accepting invitation...');
      try {
        await workspaceApi.acceptWorkspaceInvitation(invitationId);
        removeReceivedInvitation(invitationId);
        // Refresh workspaces list to include the new workspace
        await fetchWorkspaces();
        toast.success('Invitation accepted');
        return true;
      } catch (error: any) {
        toast.error(error.message || 'Failed to accept invitation');
        return false;
      } finally {
        hideLoader();
      }
    },
    [removeReceivedInvitation, fetchWorkspaces, showLoader, hideLoader]
  );

  /**
   * Reject workspace invitation
   */
  const rejectInvitation = useCallback(
    async (invitationId: string) => {
      showLoader('Rejecting invitation...');
      try {
        await workspaceApi.rejectWorkspaceInvitation(invitationId);
        removeReceivedInvitation(invitationId);
        toast.success('Invitation rejected');
        return true;
      } catch (error: any) {
        toast.error(error.message || 'Failed to reject invitation');
        return false;
      } finally {
        hideLoader();
      }
    },
    [removeReceivedInvitation, showLoader, hideLoader]
  );

  /**
   * Cancel workspace invitation
   */
  const cancelInvitation = useCallback(
    async (invitationId: string) => {
      showLoader('Canceling invitation...');
      try {
        await workspaceApi.cancelWorkspaceInvitation(invitationId);
        removeWorkspaceInvitation(invitationId);
        toast.success('Invitation canceled');
        return true;
      } catch (error: any) {
        toast.error(error.message || 'Failed to cancel invitation');
        return false;
      } finally {
        hideLoader();
      }
    },
    [removeWorkspaceInvitation, showLoader, hideLoader]
  );

  // ========== Workspace Projects & Dashboard ==========

  /**
   * Fetch workspace projects
   */
  const fetchWorkspaceProjects = useCallback(
    async (workspaceId: string) => {
      setLoadingProjects(true);
      try {
        const projects = await workspaceApi.getWorkspaceProjects(workspaceId);
        setWorkspaceProjects(projects);
        return projects;
      } catch (error: any) {
        toast.error(error.message || 'Failed to fetch projects');
        return [];
      } finally {
        setLoadingProjects(false);
      }
    },
    [setWorkspaceProjects, setLoadingProjects]
  );

  /**
   * Fetch workspace dashboard
   */
  const fetchWorkspaceDashboard = useCallback(
    async (workspaceId: string) => {
      setLoadingDashboard(true);
      try {
        const dashboard = await workspaceApi.getWorkspaceDashboard(workspaceId);
        setWorkspaceDashboard(dashboard);
        return dashboard;
      } catch (error: any) {
        toast.error(error.message || 'Failed to fetch dashboard');
        return null;
      } finally {
        setLoadingDashboard(false);
      }
    },
    [setWorkspaceDashboard, setLoadingDashboard]
  );

  return {
    // Workspace CRUD
    fetchWorkspaces,
    fetchWorkspaceById,
    fetchWorkspaceBySlug,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    setSelectedWorkspace,

    // Members
    fetchWorkspaceMembers,
    addWorkspaceMember,
    updateMemberRole,
    removeWorkspaceMember,
    leaveWorkspace,

    // Invitations
    inviteUserToWorkspace,
    fetchWorkspaceInvitations,
    fetchReceivedInvitations,
    acceptInvitation,
    rejectInvitation,
    cancelInvitation,

    // Projects & Dashboard
    fetchWorkspaceProjects,
    fetchWorkspaceDashboard,
  };
};

export default useWorkspacesHook;
